import { createFileRoute, redirect } from "@tanstack/react-router"
import { useCallback, useState } from "react"
import { loginSchema } from "@/schemas/onboarding/loginSchema"
import {
  Button,
  Input,
  MicrosoftSSO,
  scrollToFirstFormError,
  Separator,
  useAppForm,
} from "toptaal-shared"
import { handleMutationError } from "toptaal-shared"
import { LoginWrapper } from "toptaal-shared"
import { useTopTaalMagicLinkMutation } from "@/api/mutations/authentication/useMagicLinkMutation"
import { useAuthStore } from "@/stores/useAuthStore"

export const Route = createFileRoute("/login/")({
  component: RouteComponent,
  beforeLoad: async () => {
    const { getToken, getRefreshToken } = useAuthStore.getState()
    if (getToken() || getRefreshToken()) {
      throw redirect({ to: "/" })
    }
  },
})

function RouteComponent() {
  const { mutateAsync, isSuccess, isPending } = useTopTaalMagicLinkMutation()
  const [error, setError] = useState<string | null>(null)

  const form = useAppForm({
    onSubmitInvalid: scrollToFirstFormError,
    validators: {
      onSubmit: loginSchema,
    },
    onSubmit: async ({ value }) => {
      await mutateAsync(value.email).catch(handleMutationError(setError))
    },
    defaultValues: {
      email: "",
    },
  })

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      setError(null)
      form.handleSubmit()
    },
    [form],
  )

  return (
    <LoginWrapper title="Inloggen">
      {isSuccess ? (
        <p className="text-center">
          We hebben een link naar je e-mailadres gestuurd waarmee je kunt
          inloggen.
        </p>
      ) : (
        <>
          <form.AppForm>
            <form
              onSubmit={handleSubmit}
              onChange={() => {
                if (error) {
                  setError(null)
                }
              }}
            >
              <form.AppField
                name="email"
                children={(field) => (
                  <field.FormItem>
                    <field.FormControl>
                      <Input
                        label="E-mailadres"
                        type="text"
                        className="w-full"
                        variant={"grey"}
                        value={field.state.value}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        autoComplete="email"
                      />
                    </field.FormControl>
                    {error && <p className="text-red text-sm mt-2">{error}</p>}

                    <field.FormMessage />
                  </field.FormItem>
                )}
              />

              <form.Subscribe>
                {({ isDirty, isValid }) => (
                  <Button
                    type="submit"
                    disabled={!isDirty || !isValid}
                    variant="ghost"
                    isLoading={isPending}
                    className="flex mx-auto mt-10"
                  >
                    Inloggen
                  </Button>
                )}
              </form.Subscribe>
            </form>
          </form.AppForm>

          <Separator className="my-8" />
          <a
            href={`${import.meta.env.VITE_API_URL}/auth/microsoft`}
            className="flex w-full border p-2 justify-center"
            rel="noopener noreferrer"
          >
            <MicrosoftSSO />
          </a>
        </>
      )}
    </LoginWrapper>
  )
}
